import api from './api';

export const authService = {
  login: async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      
      // Check if MFA is required - return early with MFA info
      if (response.data.mfaRequired) {
        return {
          mfaRequired: true,
          mfaToken: response.data.mfaToken
        };
      }
      
      const { token, ...userData } = response.data;
      
      // Extract data from JWT token
      const decodedToken = parseJwt(token);
      const userRole = decodedToken?.Role || 'Employee';
      
      // Get isTrialActive from JWT (it's a string "true"/"false")
      const isTrialActiveFromJwt = decodedToken?.IsTrialActive === 'true' || decodedToken?.IsTrialActive === true;
      
      // Get subscriptionPlan from JWT
      const subscriptionPlanFromJwt = decodedToken?.SubscriptionPlan || userData.subscriptionPlan || 'Solo';
      
      // Create user object with all fields including role and trial info
      const user = {
        userId: userData.userId,
        businessId: userData.businessId,
        businessName: userData.businessName,
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        subscriptionStatus: userData.subscriptionStatus,
        subscriptionPlan: subscriptionPlanFromJwt,
        hasValidAccess: userData.hasValidAccess,
        trialEndDate: userData.trialEndDate,
        trialDaysRemaining: userData.trialDaysRemaining,
        trialStartDate: userData.trialStartDate,
        // ✅ NEW: isTrialActive from JWT
        isTrialActive: isTrialActiveFromJwt,
        // Founding member flag - drives founder pricing UI throughout the app
        isFoundingMember: userData.isFoundingMember === true,
        workflowMode: userData.workflowMode || 'Standard',
        // Industry information
        primaryServiceType: userData.primaryServiceType,
        additionalServiceTypes: userData.additionalServiceTypes || [],
        serviceTypesSeeded: userData.serviceTypesSeeded || 0,
        // Role from JWT
        role: userRole
      };
      
      // Don't store in localStorage yet - let BusinessContext validate first
      return { token, user };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Login failed';
      const cleanError = new Error(errorMessage);
      cleanError.originalError = error;
      throw cleanError;
    }
  },

  register: async (userData) => {
    try {
      const response = await api.post('/register', userData);
      
      const { 
        token, 
        userId, 
        businessId, 
        businessName, 
        email, 
        trialStartDate, 
        trialEndDate, 
        trialDaysRemaining,
        primaryServiceType,
        additionalServiceTypes,
        serviceTypesSeeded
      } = response.data;
      
      // Extract from JWT token
      const decodedToken = parseJwt(token);
      const userRole = decodedToken?.Role || 'Owner';
      const isTrialActiveFromJwt = decodedToken?.IsTrialActive === 'true' || decodedToken?.IsTrialActive === true;
      const subscriptionPlanFromJwt = decodedToken?.SubscriptionPlan || 'Solo';
      
      const user = {
        userId,
        businessId,
        businessName,
        email,
        subscriptionStatus: 'Trial',
        subscriptionPlan: subscriptionPlanFromJwt,
        hasValidAccess: true,
        trialEndDate,
        trialDaysRemaining,
        trialStartDate,
        // ✅ NEW: Registration always starts with active trial
        isTrialActive: true,
        // Founding member flag - always false for fresh registrations
        // (gets set later via admin toggle)
        isFoundingMember: false,
        // Industry information
        primaryServiceType,
        additionalServiceTypes: additionalServiceTypes || [],
        serviceTypesSeeded: serviceTypesSeeded || 0,
        // Role from JWT
        role: userRole
      };
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      return { token, user };
    } catch (error) {
      let errorMessage = 'Registration failed';
      
      if (error.response?.data?.reasons && Array.isArray(error.response.data.reasons)) {
        errorMessage = error.response.data.reasons.join('. ');
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      const cleanError = new Error(errorMessage);
      cleanError.response = error.response;
      cleanError.originalError = error;
      throw cleanError;
    }
  },

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  getToken: () => {
    return localStorage.getItem('token');
  },

  getUser: () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  isAuthenticated: () => {
    const token = authService.getToken();
    const user = authService.getUser();
    
    if (!token || !user) {
      return false;
    }

    // Check if trial has expired (if in trial status)
    if (user.subscriptionStatus === 'Trial') {
      if (!user.hasValidAccess) {
        authService.logout();
        return false;
      }
    }

    // Check if subscription is valid
    if (!user.hasValidAccess) {
      authService.logout();
      return false;
    }

    return true;
  },

  // Helper to check trial status
  getTrialStatus: () => {
    const user = authService.getUser();
    if (!user) return null;

    return {
      status: user.subscriptionStatus,
      plan: user.subscriptionPlan,
      trialEndDate: user.trialEndDate,
      daysRemaining: user.trialDaysRemaining,
      hasValidAccess: user.hasValidAccess,
      isTrialActive: user.isTrialActive || (user.subscriptionStatus === 'Trial' && user.hasValidAccess),
      isTrialExpired: user.subscriptionStatus === 'Expired' || 
                      (user.subscriptionStatus === 'Trial' && !user.hasValidAccess)
    };
  },

  // Helper to get industry information
  getIndustryInfo: () => {
    const user = authService.getUser();
    if (!user) return null;

    return {
      primaryServiceType: user.primaryServiceType,
      additionalServiceTypes: user.additionalServiceTypes || [],
      serviceTypesSeeded: user.serviceTypesSeeded || 0
    };
  },

  // Helper to get user role
  getUserRole: () => {
    const user = authService.getUser();
    return user?.role || null;
  },

  // Helper to check if user is owner
  isOwner: () => {
    const role = authService.getUserRole();
    return role === 'Owner';
  },

  // Helper to check if user is employee
  isEmployee: () => {
    const role = authService.getUserRole();
    return role === 'Employee';
  },

  // Helper to check if user is application admin
  isApplicationAdmin: () => {
    const role = authService.getUserRole();
    return role === 'ApplicationAdmin';
  }
};

// Helper function to decode JWT token
function parseJwt(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error parsing JWT:', error);
    return null;
  }
}